import React from 'react';
import { Form, InlineNotification } from '@carbon/react';
import { Information } from '@carbon/react/icons';

interface StepLayoutProps {
  title: string;
  subtitle?: string;
  /** Rendered into the pinned footer — the step owns its own button set. */
  actions: React.ReactNode;
  /** Explains what the step's answers are used for. Sits after the last field, where
   * the user looks once the form is filled in rather than before they start. */
  help?: React.ReactNode;
  error?: Error | null;
  /** Matches Formik's `handleSubmit` signature, which makes the event optional. Steps
   * that do not submit a form omit it and get a plain container instead. */
  onSubmit?: (event?: React.FormEvent<HTMLFormElement>) => void;
  children: React.ReactNode;
}

const StepLayout = ({ title, subtitle, actions, help, error, onSubmit, children }: StepLayoutProps) => {
  const body = (
    <>
      <header className="onboarding-step__header">
        <h2 className="onboarding-step__title">{title}</h2>
        {subtitle && <p className="onboarding-step__subtitle">{subtitle}</p>}
      </header>

      <div className="onboarding-step__body">
        {children}
        {help && (
          <p className="onboarding-step__help" data-testid="onboarding-step-help">
            {/* Decorative: the sentence beside it already carries the meaning, so an
                accessible name here would only be read out twice. */}
            <Information size={16} aria-hidden className="onboarding-step__help-icon" />
            <span>{help}</span>
          </p>
        )}
        {error && (
          <div className="onboarding-step__error" data-testid="onboarding-step-error">
            <InlineNotification kind="error" title={error.message} lowContrast hideCloseButton />
          </div>
        )}
      </div>

      <footer className="onboarding-step__actions">{actions}</footer>
    </>
  );

  if (!onSubmit) {
    return <div className="onboarding-step">{body}</div>;
  }

  return (
    <Form className="onboarding-step" onSubmit={onSubmit}>
      {body}
    </Form>
  );
};

export default StepLayout;
